import React, { useEffect, useMemo, useRef, useState } from "react";
import { ChevronDown, ChevronLeft, ChevronRight } from "lucide-react";

const WEEKDAYS = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];
const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

const pad = (n) => String(n).padStart(2, "0");
const toIso = (y, m, d) => `${y}-${pad(m + 1)}-${pad(d)}`;

const parseIso = (value) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(value || ""));
  if (!m) return null;
  return { y: Number(m[1]), m: Number(m[2]) - 1, d: Number(m[3]) };
};

const todayIso = () => {
  const now = new Date();
  return toIso(now.getFullYear(), now.getMonth(), now.getDate());
};

const formatTrigger = (iso) => {
  const parts = parseIso(iso);
  if (!parts) return "Select date";
  return `${MONTHS[parts.m].slice(0, 3)} ${parts.d}, ${parts.y}`;
};

// Reusable month-grid dropdown. Pick a day → onPick(iso). Used by the dispatch
// header and (site-wide) the floating subnav; picking always routes to that
// day's dispatch via the caller's onPick.
export default function CalendarDropdown({ value, onPick, className = "" }) {
  const [open, setOpen] = useState(false);
  const anchor = useMemo(() => parseIso(value) || parseIso(todayIso()), [value]);
  const [viewYear, setViewYear] = useState(anchor.y);
  const [viewMonth, setViewMonth] = useState(anchor.m);
  const rootRef = useRef(null);

  useEffect(() => {
    if (open) {
      setViewYear(anchor.y);
      setViewMonth(anchor.m);
    }
  }, [open, anchor.y, anchor.m]);

  useEffect(() => {
    const onDown = (event) => {
      if (!rootRef.current?.contains(event.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, []);

  const grid = useMemo(() => {
    const firstDow = new Date(viewYear, viewMonth, 1).getDay();
    const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();
    const cells = [];
    for (let i = 0; i < firstDow; i += 1) cells.push(null);
    for (let d = 1; d <= daysInMonth; d += 1) cells.push(d);
    return cells;
  }, [viewYear, viewMonth]);

  const shiftMonth = (delta) => {
    let m = viewMonth + delta;
    let y = viewYear;
    if (m < 0) { m = 11; y -= 1; }
    if (m > 11) { m = 0; y += 1; }
    setViewMonth(m);
    setViewYear(y);
  };

  const today = todayIso();

  return (
    <div ref={rootRef} className={`dispatch-calendar-dd ${className}`}>
      <button
        type="button"
        className="dispatch-date-display"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-haspopup="dialog"
      >
        {formatTrigger(value)}
        <ChevronDown size={14} />
      </button>

      {open ? (
        <div className="dispatch-calendar-pop" role="dialog" aria-label="Pick a date">
          <div className="dispatch-calendar-pop-head">
            <button type="button" onClick={() => shiftMonth(-1)} aria-label="Previous month">
              <ChevronLeft size={16} />
            </button>
            <span>{MONTHS[viewMonth]} {viewYear}</span>
            <button type="button" onClick={() => shiftMonth(1)} aria-label="Next month">
              <ChevronRight size={16} />
            </button>
          </div>
          <div className="dispatch-calendar-weekdays">
            {WEEKDAYS.map((w) => (
              <span key={w}>{w}</span>
            ))}
          </div>
          <div className="dispatch-calendar-grid">
            {grid.map((d, index) => {
              if (d === null) return <span key={`e${index}`} className="dispatch-calendar-cell is-empty" />;
              const iso = toIso(viewYear, viewMonth, d);
              return (
                <button
                  key={iso}
                  type="button"
                  className={[
                    "dispatch-calendar-cell",
                    iso === value ? "is-selected" : "",
                    iso === today ? "is-today" : "",
                  ].filter(Boolean).join(" ")}
                  onClick={() => {
                    onPick?.(iso);
                    setOpen(false);
                  }}
                >
                  {d}
                </button>
              );
            })}
          </div>
          <button
            type="button"
            className="dispatch-calendar-today"
            onClick={() => {
              onPick?.(today);
              setOpen(false);
            }}
          >
            Today
          </button>
        </div>
      ) : null}
    </div>
  );
}
